// User avatar + profile dropdown for the topbar. Pulls real session data
// from /api/me (Entra-derived: full_name, email, avatar_url, role).
//
// Menu items: identity card, theme switcher, link to admin (if admin),
// sign out.

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Sun, Moon, Monitor, LogOut, Settings, Shield,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { meApi } from "@/lib/api";
import { useDarkMode } from "@/hooks/useDarkMode";
import { initials, avatarColor } from "@/lib/utils";

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrator",
  sales: "Sales",
  marketing: "Marketing",
  readonly: "Read-only",
};

export default function ProfileMenu() {
  const navigate = useNavigate();
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => meApi.get(),
    staleTime: 5 * 60_000,
  });
  const { theme, setTheme } = useDarkMode();

  const displayName = me?.full_name ?? me?.given_name ?? me?.email ?? "";
  const subtitle = me?.email ?? "";
  const avatarSeed = me?.email ?? me?.full_name ?? "user";
  const avatarFallback = initials(me?.full_name ?? me?.given_name ?? me?.email);
  const isAdmin = me?.role === "admin";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="group rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-panel"
          aria-label="Profile menu"
        >
          <Avatar className="h-8 w-8 ring-1 ring-line transition-all group-hover:ring-2 group-hover:ring-brand/40">
            {me?.avatar_url && <AvatarImage src={me.avatar_url} alt={displayName} />}
            <AvatarFallback className={avatarColor(avatarSeed)}>
              {avatarFallback}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        {/* Identity card */}
        <div className="flex items-center gap-3 px-2 py-2">
          <Avatar className="h-10 w-10">
            {me?.avatar_url && <AvatarImage src={me.avatar_url} alt={displayName} />}
            <AvatarFallback className={avatarColor(avatarSeed)}>
              {avatarFallback}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{displayName}</p>
            <p className="truncate text-[11px] text-ink-dim">{subtitle}</p>
            {me?.role && (
              <Badge variant={isAdmin ? "default" : "muted"} className="mt-1 capitalize">
                {ROLE_LABEL[me.role] ?? me.role}
              </Badge>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />

        {/* Theme switcher */}
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as never)}>
          <DropdownMenuRadioItem value="light">
            <Sun className="mr-2 h-3.5 w-3.5" /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="mr-2 h-3.5 w-3.5" /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="mr-2 h-3.5 w-3.5" /> Follow system
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />

        {/* Quick links */}
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate("/admin/users")}>
            <Shield className="mr-2 h-3.5 w-3.5" /> Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate("/admin/branding")}>
          <Settings className="mr-2 h-3.5 w-3.5" /> Workspace settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        <DropdownMenuItem destructive asChild>
          <a href="/crm/auth/logout" className="flex w-full items-center">
            <LogOut className="mr-2 h-3.5 w-3.5" /> Sign out
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
